import {
  type CollectionReference,
  type DocumentData,
  type Firestore,
  arrayRemove,
  arrayUnion,
  doc,
  getDoc,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { SansPersonne } from '@taki/data-model';

import type { AccesDeListe, CadrageAcces } from './list-query.js';

/**
 * L'ACCES PAR FICHE, cote SDK : ce que les repositories des familles porteuses
 * (`contents`, `conversations`) et heritieres (`messages`) partagent.
 * Le raisonnement : `docs/socle/acces-par-fiche.md`.
 *
 * Trois choses, et rien d'autre :
 *  - `creerMaPersonne` : le `personId` de l'appelant, lu UNE fois sur sa fiche
 *    membre et garde, oublie si le compte change ;
 *  - `cadrageAcces` : ce que `planListQuery` doit savoir pour traduire l'option
 *    `acces` (le role de la famille, et le `personId` quand la liste est privee) ;
 *  - `createAccesOps` : `partager`, `retirer`, `quitter`, les trois gestes qui
 *    changent les participants d'une fiche privee, ecrits une fois pour les
 *    familles porteuses, sur le modele de `archive-ops.ts`.
 */

/** Le `personId` de l'appelant, ou `null` sans fiche membre (ou sans `personId`). */
export type MaPersonne = () => Promise<string | null>;

/**
 * Le resolveur du `personId` de l'appelant. Une lecture (sa fiche membre, que
 * les rules lui laissent lire), gardee tant que le compte ne change pas : une
 * liste privee en a besoin a chaque appel, et la relire a chaque fois
 * doublerait le prix de chaque page. Un echec de lecture n'est pas garde.
 */
export function creerMaPersonne(
  getActorEmail: () => string | null,
  lireMaFiche: () => Promise<{ personId?: string | null | undefined } | null>,
): MaPersonne {
  let cache: { email: string; promesse: Promise<string | null> } | null = null;
  return async () => {
    const email = getActorEmail();
    if (email === null || email.length === 0) return null;
    if (cache === null || cache.email !== email) {
      const promesse = lireMaFiche().then(
        (fiche) =>
          fiche !== null && typeof fiche.personId === 'string' && fiche.personId.length > 0
            ? fiche.personId
            : null,
        (cause: unknown) => {
          cache = null;
          throw cause;
        },
      );
      cache = { email, promesse };
    }
    return cache.promesse;
  };
}

/**
 * Le cadrage d'une liste, d'un compte ou d'un agregat d'une famille a acces
 * par fiche. Le `personId` n'est resolu (une lecture, gardee) que pour une
 * liste PRIVEE : une liste d'equipe, d'ancre ou de conversation n'en a pas
 * besoin, et un compte sans fiche membre doit continuer de lire l'equipe.
 */
export async function cadrageAcces(
  role: CadrageAcces['role'],
  acces: AccesDeListe | undefined,
  maPersonne: MaPersonne,
): Promise<CadrageAcces> {
  return { role, personId: acces === 'prive' ? await maPersonne() : null };
}

/** Les trois gestes sur les participants d'une fiche privee. */
export interface AccesOps<T> {
  /** Ajoute des personnes (`personId`) a une fiche privee. Reserve au proprietaire (rules). Rend la fiche relue. */
  partager(id: string, personIds: readonly string[]): Promise<T>;
  /** Retire des personnes d'une fiche privee. Reserve au proprietaire, qui ne se retire pas (rules). */
  retirer(id: string, personIds: readonly string[]): Promise<T>;
  /**
   * L'appelant se retire d'une fiche privee qu'on lui a partagee. Le
   * proprietaire ne peut pas (rules). Resout quand le depart est ECRIT, sans
   * rendre la fiche : des qu'il est parti, l'appelant ne peut plus la lire, et
   * la relire annoncerait en echec un depart qui a reussi.
   */
  quitter(id: string): Promise<void>;
}

function verifierPersonnes(personIds: readonly string[], geste: string): string[] {
  if (!Array.isArray(personIds) || personIds.length === 0) {
    throw new Error(`${geste} : au moins un personId est attendu.`);
  }
  for (const personId of personIds) {
    if (typeof personId !== 'string' || personId.length === 0) {
      throw new Error(`${geste} : un personId est une chaine non vide (recu : ${String(personId)}).`);
    }
  }
  return [...new Set(personIds)];
}

/**
 * @param db - l'instance Firestore, pour ecrire par une ref BRUTE.
 * @param col - la collection AVEC son converter, pour relire la fiche validee.
 * @param collectionName - le nom de la collection, pour la ref brute et les messages.
 * @param getActorId - l'uid de l'appelant (les rules exigent `updatedBy == request.auth.uid`).
 * @param maPersonne - le `personId` de l'appelant, pour `quitter`.
 */
export function createAccesOps<T>(
  db: Firestore,
  col: CollectionReference<T>,
  collectionName: string,
  getActorId: () => string,
  maPersonne: MaPersonne,
): AccesOps<T> {
  async function ecrirePuisRelire(id: string, geste: string, patch: DocumentData): Promise<T> {
    // La liste des participants se modifie PAR OPERATEUR (`arrayUnion`,
    // `arrayRemove`), jamais en la reecrivant : deux partages concurrents
    // s'ajoutent au lieu de s'ecraser. Les rules jugent le resultat.
    await updateDoc(doc(db, collectionName, id), {
      ...patch,
      updatedAt: serverTimestamp(),
      updatedBy: getActorId(),
    });
    const snap = await getDoc(doc(col, id));
    if (!snap.exists()) {
      throw new Error(`${collectionName} ${id} introuvable apres ${geste}.`);
    }
    return snap.data();
  }

  return {
    async partager(id, personIds) {
      const ids = verifierPersonnes(personIds, `${collectionName}.partager`);
      return ecrirePuisRelire(id, 'partage', { 'acces.participants': arrayUnion(...ids) });
    },

    async retirer(id, personIds) {
      const ids = verifierPersonnes(personIds, `${collectionName}.retirer`);
      return ecrirePuisRelire(id, 'retrait', { 'acces.participants': arrayRemove(...ids) });
    },

    async quitter(id) {
      const moi = await maPersonne();
      if (moi === null) throw new SansPersonne();
      // PAS DE RELECTURE : la fiche est fermee a l'appelant des que l'ecriture
      // passe (il n'est plus participant). Un `updateDoc` qui resout EST le
      // succes ; un refus des rules (pas participant, proprietaire) rejette.
      await updateDoc(doc(db, collectionName, id), {
        'acces.participants': arrayRemove(moi),
        updatedAt: serverTimestamp(),
        updatedBy: getActorId(),
      });
    },
  };
}
